import type { Lead } from "@/types/lead";
import type { AdsActivityFreshAchievement } from "@/lib/adsAssistantClientEngagement";
import type { FreshAchievementsContextValue } from "@/contexts/FreshAchievementsContext";
import { runLeadAchievementCheck } from "@/lib/leadAchievementCheckClient";

export function isViableLeadForAchievement(l: Lead): boolean {
  const e = l.extracted;
  if (!e) return false;
  const hasName = !!e.first_name?.trim() && !!e.last_name?.trim();
  const hasContact = !!e.phone?.trim() || !!e.email?.trim();
  const hasProduct = !!e.product_line;
  return hasName && hasContact && hasProduct;
}

/** Fire achievement check when a non-test lead is newly synced or becomes viable. */
export function shouldFireLeadAchievementCheck(
  prevSynced: Set<string>,
  rows: Array<Record<string, unknown>>,
  leadsById: Map<string, Lead>,
  prevViable: ReadonlyMap<string, boolean>,
): boolean {
  for (const row of rows) {
    if (row.is_test === true) continue;
    const id = String(row.id ?? "");
    if (!id) continue;
    const lead = leadsById.get(id);
    if (!lead || lead.isTest) continue;
    const viable = isViableLeadForAchievement(lead);
    if (!viable) continue;
    const wasSynced = prevSynced.has(id);
    const wasViable = prevViable.get(id) ?? false;
    if (!wasSynced || !wasViable) return true;
  }
  return false;
}

export function fireLeadAchievementCheck(fresh: FreshAchievementsContextValue | null): void {
  void (async () => {
    try {
      const result = await runLeadAchievementCheck();
      if (result.ok && result.fresh_achievements?.length && fresh) {
        fresh.publish(result.fresh_achievements);
      }
    } catch (err) {
      console.warn("[lead-achievement-check] client call failed", err);
    }
  })();
}

export function publishLeadFreshAchievements(
  fresh: FreshAchievementsContextValue | null,
  rows?: AdsActivityFreshAchievement[],
): void {
  if (rows?.length && fresh) fresh.publish(rows);
}

export function afterSuccessfulLeadUpsert(
  prevSynced: Set<string>,
  upsertedRows: Array<Record<string, unknown>>,
  currentLeads: Lead[],
  prevViable: ReadonlyMap<string, boolean>,
  fresh: FreshAchievementsContextValue | null,
): void {
  const leadsById = new Map(currentLeads.map((l) => [l.id, l]));
  if (shouldFireLeadAchievementCheck(prevSynced, upsertedRows, leadsById, prevViable)) {
    fireLeadAchievementCheck(fresh);
  }
}
